import { useState } from "react";
import { format } from "date-fns";
import { api } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  FileText,
  Download,
  CalendarIcon,
  Thermometer,
  Package,
  Loader2,
  Wind,
} from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const { t } = useI18n();
  const [reportType, setReportType] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [previewData, setPreviewData] = useState({ products: [], temperature_logs: [], cooling_records: [] });
  const [hasPreview, setHasPreview] = useState(false);

  const formattedStart = startDate ? format(startDate, "yyyy-MM-dd") : "";
  const formattedEnd = endDate ? format(endDate, "yyyy-MM-dd") : "";

  const canGenerate = !!startDate && !!endDate;
  const canDownload = canGenerate && hasPreview;

  const buildParams = () => {
    const params = {};
    if (formattedStart) params.start_date = formattedStart;
    if (formattedEnd) params.end_date = formattedEnd;
    if (reportType && reportType !== "all") params.report_type = reportType;
    else params.report_type = "all";
    return params;
  };

  const handleGenerate = async () => {
    if (!canGenerate) {
      toast.error("Please select both start and end dates");
      return;
    }
    setPreviewLoading(true);
    try {
      const response = await api.get("/reports/summary", { params: buildParams() });
      setPreviewData(response.data || { products: [], temperature_logs: [], cooling_records: [] });
      setHasPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate report preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!canDownload) {
      toast.error("Generate the report preview before downloading");
      return;
    }
    setDownloadLoading(true);
    try {
      const response = await api.get("/reports/summary/pdf", {
        params: buildParams(),
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const safeStart = formattedStart || "start";
      const safeEnd = formattedEnd || "end";
      link.href = url;
      link.download = `report_${safeStart}_to_${safeEnd}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF report downloaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download PDF report");
    } finally {
      setDownloadLoading(false);
    }
  };

  const products = previewData.products || [];
  const logs = previewData.temperature_logs || [];
  const purchaseItems = previewData.purchase_list || [];
  const coolingRecords = previewData.cooling_records || [];

  return (
    <div data-testid="reports-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground">{t("reports.title")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("reports.subtitle")}
          </p>
        </div>
      </div>

      {/* Filters and Actions */}
      <Card className="">
        <CardHeader>
          <CardTitle className="font-heading text-lg">{t("reports.filtersTitle")}</CardTitle>
          <CardDescription>{t("reports.filtersDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            {/* Start Date */}
            <div className="space-y-2">
              <Label>{t("reports.startDate")}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                    data-testid="report-start-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Select start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label>{t("reports.endDate")}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                    data-testid="report-end-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Select end date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Report Type */}
            <div className="space-y-2">
              <Label>{t("reports.reportType")}</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger data-testid="report-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("reports.typeAll")}</SelectItem>
                  <SelectItem value="products">{t("reports.typeProducts")}</SelectItem>
                  <SelectItem value="temperature">{t("reports.typeTemperature")}</SelectItem>
                  <SelectItem value="cooling">{t("reports.typeCooling")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={handleGenerate}
              disabled={previewLoading || !canGenerate}
              data-testid="generate-report-btn"
            >
              {previewLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              {previewLoading ? t("reports.generating") : t("reports.generate")}
            </Button>
            <Button
              onClick={handleDownloadPdf}
              disabled={downloadLoading || !canDownload || !hasPreview}
              className=""
              data-testid="download-report-pdf-btn"
            >
              {downloadLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {downloadLoading ? t("reports.preparingPdf") : t("reports.downloadPdf")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Section */}
      {hasPreview && (
        <div className="space-y-6" data-testid="report-preview">
          {reportType !== "temperature" && (
            <Card className="">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="font-heading text-lg">{t("reports.productsTitle")}</CardTitle>
                    <CardDescription>
                      Products with expiration dates between {formattedStart} and {formattedEnd}.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {products.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("reports.noProducts")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-zinc-50">
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colName")}</th>
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colCategory")}</th>
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colLocation")}</th>
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colExpiration")}</th>
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colStatus")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((p, idx) => (
                          <tr key={p.product_id || `prod-${idx}`} className="border-b border-slate-100">
                            <td className="py-2 px-3">{p.name}</td>
                            <td className="py-2 px-3">{p.category}</td>
                            <td className="py-2 px-3">{p.storage_location}</td>
                            <td className="py-2 px-3">{p.expiration_date}</td>
                            <td className="py-2 px-3 capitalize">{p.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {reportType !== "products" && (
            <Card className="">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Thermometer className="h-5 w-5 text-sky-600" />
                  <div>
                    <CardTitle className="font-heading text-lg">{t("reports.tempLogsTitle")}</CardTitle>
                    <CardDescription>
                      Temperature readings between {formattedStart} and {formattedEnd}.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("reports.noTempLogs")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-zinc-50">
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colDateTime")}</th>
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colRefrigerator")}</th>
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colTemp")}</th>
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colInRange")}</th>
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colNotes")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log, idx) => (
                          <tr key={log.log_id || `log-${idx}`} className="border-b border-slate-100">
                            <td className="py-2 px-3">{log.logged_at}</td>
                            <td className="py-2 px-3">{log.refrigerator}</td>
                            <td className="py-2 px-3">{log.temperature}</td>
                            <td className="py-2 px-3">{log.is_within_range ? "Yes" : "No"}</td>
                            <td className="py-2 px-3">{log.notes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Purchase List Preview */}
          {reportType !== "temperature" && (
            <Card className="">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Download className="h-5 w-5 text-orange-500" />
                  <div>
                    <CardTitle className="font-heading text-lg">{t("reports.purchaseTitle")}</CardTitle>
                    <CardDescription>
                      Items currently pending from recent closing checklists.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {purchaseItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("reports.noPurchase")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-zinc-50">
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colItem")}</th>
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colStatus")}</th>
                          <th className="py-2 px-3 text-left font-semibold text-foreground">{t("reports.colCreated")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseItems.map((p, idx) => (
                          <tr key={p.item_id || p.id || `purchase-${idx}`} className="border-b border-slate-100">
                            <td className="py-2 px-3">{p.name}</td>
                            <td className="py-2 px-3 capitalize">{p.status}</td>
                            <td className="py-2 px-3">{p.created_at}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          {/* Cooling Records Preview */}
          {(reportType === "all" || reportType === "cooling") && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Wind className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="font-heading text-lg">{t("reports.coolingTitle")}</CardTitle>
                    <CardDescription>
                      {t("reports.coolingDesc").replace("{start}", formattedStart).replace("{end}", formattedEnd)}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {coolingRecords.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("reports.noCooling")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-zinc-50">
                          <th className="py-2 px-3 text-left font-semibold">{t("reports.colProduct")}</th>
                          <th className="py-2 px-3 text-left font-semibold">{t("reports.colDate")}</th>
                          <th className="py-2 px-3 text-left font-semibold">{t("reports.colEntryTemp")}</th>
                          <th className="py-2 px-3 text-left font-semibold">{t("reports.colEntryTime")}</th>
                          <th className="py-2 px-3 text-left font-semibold">{t("reports.colExitTemp")}</th>
                          <th className="py-2 px-3 text-left font-semibold">{t("reports.colExitTime")}</th>
                          <th className="py-2 px-3 text-left font-semibold">{t("reports.colStatus")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coolingRecords.map((cr, idx) => (
                          <tr key={cr.cooling_id || cr.id || `cooling-${idx}`} className="border-b border-slate-100">
                            <td className="py-2 px-3 font-medium">{cr.product_name}</td>
                            <td className="py-2 px-3">{cr.date}</td>
                            <td className="py-2 px-3">{cr.entry_temperature != null ? `${cr.entry_temperature}°C` : "—"}</td>
                            <td className="py-2 px-3">{cr.entry_time || "—"}</td>
                            <td className="py-2 px-3">{cr.exit_temperature != null ? `${cr.exit_temperature}°C` : "—"}</td>
                            <td className="py-2 px-3">{cr.exit_time || "—"}</td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                cr.status === "complete" ? "bg-emerald-50 text-emerald-700" :
                                cr.status === "issue"    ? "bg-red-50 text-red-700" :
                                                           "bg-amber-50 text-amber-700"
                              }`}>{cr.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
